import React, { useEffect, useRef, useState } from 'react';
import { CTAButton } from './CTAButton';

// Hero "Cards": el Protocolo H.E.R. es el protagonista. Debajo, "Todo lo que incluye H.E.R.":
// un carrusel de cards que se mueve solo (y se puede arrastrar), con las portadas reales.
// Las cards son solo imagen y texto: no abren previews. La exploración interactiva vive en
// la sección "El método H·E·R" (components/method).
// El carrusel se mueve solo aunque el celular tenga "reducir movimiento" (muchos lo traen activado): es un
// desplazamiento lento y horizontal, sin destellos ni parpadeos.

const CDN = 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/';
const R2 = 'https://archivos.simpleymejor.com/';
const PROTOCOLO_COVER = R2 + 'httpsarchivos.simpleymejor.comhercoversher-protocolo-cover.png.png';
const DULCE_COVER = R2 + 'httpsarchivos.simpleymejor.comhercoversher-dulce-cover.png.png';
const RECETARIO_COVER = R2 + 'hercoversher-recetario-cover.png.png';
const GUIA_NAME = 'Guía H·E·R· en cobre y negro.png';
const GUIA_COVERS = [R2 + 'her-guia-cover.png', R2 + encodeURIComponent(GUIA_NAME), R2 + encodeURIComponent(GUIA_NAME.normalize('NFD')), R2 + 'her-ebooks/' + encodeURIComponent(GUIA_NAME), R2 + encodeURIComponent('Guía H.E.R. en cobre y negro.png')];
type CardItem = {
  module: string;
  title: string;
  benefit: string;
  imgs: string[];
  pos: string;
};
const cards: CardItem[] = [{
  module: 'guia',
  title: 'Guía H.E.R.',
  benefit: '✓ 9 módulos para entender esta etapa',
  imgs: GUIA_COVERS,
  pos: 'object-[center_30%]'
}, {
  module: 'recetario',
  title: 'Recetario H.E.R.',
  benefit: '✓ Comidas simples para 21 días',
  imgs: [RECETARIO_COVER],
  pos: 'object-[center_30%]'
}, {
  module: 'movement',
  title: 'H.E.R. Movement',
  benefit: '✓ Movimiento suave, a tu ritmo',
  imgs: [CDN + 'S8SNVUZHdzf8t092yIlAK_430x.jpg?v=1780543245'],
  pos: 'object-[center_34%]'
}, {
  module: 'noche',
  title: 'Noche en Calma',
  benefit: '✓ 7 noches para preparar tu descanso',
  imgs: [CDN + 'qushb3WzLl3BYpA9n1eL_430x.jpg?v=1780543244'],
  pos: 'object-[center_42%]'
}, {
  module: 'tracker',
  title: 'Tracker Premium',
  benefit: '✓ Registrá cómo te sentís',
  imgs: [CDN + 'v5gVLGm07qY3WP7hCr5Mp_430x.jpg?v=1780543245'],
  pos: 'object-[center_18%]'
}, {
  module: 'dulce',
  title: 'H.E.R. Dulce',
  benefit: '✓ 36 postres para disfrutar lo dulce',
  imgs: [DULCE_COVER],
  pos: 'object-[center_30%]'
}];
function CardImage({
  srcs,
  alt,
  className,
  label
}: {
  srcs: string[];
  alt: string;
  className: string;
  label: string;
}) {
  const [i, setI] = useState(0);
  if (i >= srcs.length) {
    return <div aria-hidden={!alt} className="flex h-full w-full items-center justify-center bg-[#090807] px-6 text-center font-display text-[24px] text-[#C8A870]" data-id="element-195" data-mp="2lnmmapw7a0hs">
        {label}
      </div>;
  }
  return <img src={srcs[i]} alt={alt} loading="lazy" decoding="async" draggable={false} onError={() => setI(n => n + 1)} className={className} data-id="element-196" data-mp="1t47mrcfqn4dn" />;
}
const SECONDS_PER_CARD = 10;
const RESUME_DELAY_MS = 1800;
export function HeroSectionCards() {
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const halfWidthRef = useRef(0);
  const isPausedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef<number | null>(null);
  const dragStartOffsetRef = useRef(0);
  const resumeTimeoutRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  useEffect(() => {
    const track = trackRef.current;
    const container = containerRef.current;
    if (!track || !container) return;
    const clearResumeTimer = () => {
      if (resumeTimeoutRef.current !== null) {
        window.clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
    };
    const scheduleResume = () => {
      clearResumeTimer();
      resumeTimeoutRef.current = window.setTimeout(() => {
        isPausedRef.current = false;
        resumeTimeoutRef.current = null;
      }, RESUME_DELAY_MS);
    };
    const measure = () => {
      halfWidthRef.current = track.scrollWidth / 2;
    };
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(track);
    const applyTransform = () => {
      track.style.transform = `translate3d(${-offsetRef.current}px,0,0)`;
    };
    const step = (ts: number) => {
      if (lastTsRef.current === null) lastTsRef.current = ts;
      const delta = ts - lastTsRef.current;
      lastTsRef.current = ts;
      const halfWidth = halfWidthRef.current;
      if (halfWidth > 0) {
        if (!isPausedRef.current && !isDraggingRef.current) {
          const pxPerMs = halfWidth / (cards.length * SECONDS_PER_CARD * 1000);
          offsetRef.current += pxPerMs * delta;
        }
        if (offsetRef.current >= halfWidth) offsetRef.current -= halfWidth;
        if (offsetRef.current < 0) offsetRef.current += halfWidth;
        applyTransform();
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    const handlePointerDown = (e: PointerEvent) => {
      isDraggingRef.current = true;
      isPausedRef.current = true;
      clearResumeTimer();
      dragStartXRef.current = e.clientX;
      dragStartOffsetRef.current = offsetRef.current;
      try {
        container.setPointerCapture(e.pointerId);
      } catch {}
    };
    const handlePointerMove = (e: PointerEvent) => {
      if (!isDraggingRef.current || dragStartXRef.current === null) return;
      const dx = e.clientX - dragStartXRef.current;
      offsetRef.current = dragStartOffsetRef.current - dx;
    };
    const endDrag = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      dragStartXRef.current = null;
      scheduleResume();
    };
    container.addEventListener('pointerdown', handlePointerDown, {
      passive: true
    });
    container.addEventListener('pointermove', handlePointerMove, {
      passive: true
    });
    container.addEventListener('pointerup', endDrag, {
      passive: true
    });
    container.addEventListener('pointercancel', endDrag, {
      passive: true
    });
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      clearResumeTimer();
      resizeObserver.disconnect();
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', endDrag);
      container.removeEventListener('pointercancel', endDrag);
    };
  }, []);
  return <section className="bg-gradient-hero relative overflow-hidden pt-8 pb-12" data-id="element-197" data-mp="1ysbjng7i7zct">
      <div className="absolute left-[8%] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/15 to-transparent" data-id="element-198" data-mp="1ttrwjch6chiv" />
      <div className="absolute right-[8%] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/15 to-transparent" data-id="element-199" data-mp="2dfa3oq417rhi" />

      <div className="relative z-10 mx-auto w-full max-w-[480px] px-5 text-center" data-id="element-200" data-mp="23gcls6142w7h">
        <div className="flex items-center gap-4 text-[#C8A870]" data-id="element-201" data-mp="23ifbsq29kpc7">
          <span className="h-px flex-1 bg-[#C8A870]/30" data-id="element-202" data-mp="28e490mqt9u5c" />
          <span className="font-display text-[30px] font-semibold leading-none tracking-[0.4em] pl-[0.4em]" data-id="element-203" data-mp="1uoaky6n4rdwo">
            H·E·R
          </span>
          <span className="h-px flex-1 bg-[#C8A870]/30" data-id="element-204" data-mp="1zgagzo02xmnz" />
        </div>
        <p className="mt-2 font-display text-[19px] text-[#F0E8D8]/90" data-id="element-205" data-mp="1kqmfomrweo09">
          21 días · 3 fases · 1 estrategia
        </p>

        <h1 className="mt-5 font-display font-semibold leading-[1.04] tracking-tight text-[#F0E8D8]" data-id="element-206" data-mp="1ppftkgb3kh0y">
          <span className="block" style={{
          fontSize: 'clamp(36px, 10.6vw, 52px)'
        }} data-id="element-207" data-mp="1bpojurrm5msr">
            Volvé a sentirte más liviana.
          </span>
          <span className="block italic text-gradient-gold my-2" style={{
          fontSize: 'clamp(28px, 8vw, 38px)',
          lineHeight: 1.08
        }} data-id="element-208" data-mp="1w6so7a4aq2hb">
            Comida, movimiento y descanso
          </span>
          <span className="block" style={{
          fontSize: 'clamp(26px, 7.4vw, 34px)'
        }} data-id="element-209" data-mp="1m71xljiuw7mi">
            en 21 días.
          </span>
        </h1>

        <p className="mx-auto mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 font-body text-[12px] font-medium uppercase tracking-[0.16em] text-[#C8A870]" data-id="element-210" data-mp="1d7eu8s6kgrqt">
          <span data-id="element-211" data-mp="18f3vhsnkbbzu">✦ Comida real</span>
          <span data-id="element-212" data-mp="13hlhvoylo8li">✦ Movimiento suave</span>
          <span data-id="element-213" data-mp="13in3uoh5mfi0">✦ Hábitos simples</span>
        </p>

        <div className="relative mx-auto mb-8 mt-8 w-full max-w-[19rem]" data-id="element-214" data-mp="13f95drbm2qfu">
          <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center" data-id="element-215" data-mp="ykbueeuru70b">
            <div className="h-[75%] w-[75%] rounded-full bg-gold/10 blur-[80px]" data-id="element-216" data-mp="tligwmfhig05" />
          </div>
          <img src={PROTOCOLO_COVER} alt="Protocolo H.E.R." width={340} height={453} loading="eager" fetchPriority="high" decoding="async" className="mx-auto block w-full max-w-[340px] rounded-[4px] shadow-[0_25px_60px_rgba(0,0,0,0.45)]" data-id="element-217" data-mp="pim1t5p5l20y" />
        </div>

        <div className="mx-auto flex w-full max-w-md flex-col items-center space-y-4" data-id="element-218" data-mp="3a5ktogrm1hq5">
          <CTAButton section="hero" onClick={scrollToOffer} data-id="element-219" data-mp="3f5gxaeweb756">
            Acceder a H.E.R. →
          </CTAButton>
          <p className="text-center font-body text-[11px] uppercase tracking-[0.14em] text-[#C8A870] md:text-[12px]" data-id="element-220" data-mp="2hhygcgskyyq3">
            ✦ Acceso inmediato&nbsp;&nbsp; ✦ Garantía 30 días
          </p>
        </div>

        <div className="mx-auto mt-14 w-full max-w-[500px] overflow-hidden" aria-label="Biblioteca H.E.R." data-id="element-221" data-mp="2rebfvvat46sr">
          <p className="mb-3 text-center uppercase tracking-[0.22em] text-[#c9a96e]" style={{
          fontSize: '15px'
        }} data-id="element-222" data-mp="2w6uumvl8d7z5">
            Biblioteca Digital H.E.R.
          </p>

          <h2 className="m-0 text-center font-serif text-[32px] leading-[1.08]" data-id="element-223" data-mp="23oqmsjkxvt5r">
            <em className="italic text-[#c9a96e]" data-id="element-224" data-mp="1f6642iziuq0v">Todo lo que incluye H.E.R.</em>
          </h2>

          <div ref={containerRef} className="relative -mx-5 mt-6 overflow-hidden px-5" style={{
          touchAction: 'pan-y',
          cursor: 'grab'
        }} data-id="element-225" data-mp="1k47w3texdzoh">
            <div ref={trackRef} className="flex w-max gap-4 will-change-transform" aria-label="Recursos incluidos en la Biblioteca Digital H.E.R." data-id="element-226" data-mp="1p1zo0nfkllso">
              {[...cards, ...cards].map((item, index) => <article key={`${item.module}-${index}`} aria-hidden={index >= cards.length} className="w-[72vw] max-w-[360px] shrink-0 overflow-hidden rounded-[18px] border border-[#c9a96e]/20 bg-[#141210]" data-id="element-227" data-mp="wd27opux5z2k" data-mpi={index}>
                  <div className="h-[220px] overflow-hidden bg-[#090807]" data-id="element-228" data-mp="16bhwgm6h4w8w">
                    <CardImage srcs={item.imgs} alt={index < cards.length ? item.title : ''} label={item.title} className={`pointer-events-none h-full w-full object-cover ${item.pos}`} data-id="element-229" data-mp="1b5ds3hz9m70w" />
                  </div>

                  <div className="px-[18px] py-4" style={{
                minHeight: '68px'
              }} data-id="element-230" data-mp="ilnl691szfqr">
                    <h3 className="sr-only" data-id="element-231" data-mp="2vvp3onubujwh">{item.title}</h3>
                    <p className="mt-1 text-center font-medium leading-snug text-[#c9a96e]" style={{
                  fontSize: '17px'
                }} data-id="element-232" data-mp="2rxrazb1clfoc">
                      {item.benefit}
                    </p>
                  </div>
                </article>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}